import React, { FC, useContext, useEffect } from 'react';
import { Element, scroller } from 'react-scroll';
import { useRouter } from 'next/router';
import CalendarItem from '@components/common/calendar/calendarItem';
import { JobPostModalContext } from '@components/common/jobPostModal/JobPostModalWrap';
import Calendar from '@components/common/calendar';
import { useHandleCalendarData } from '@components/jobs/bigCalendar/hook';
import { BigCalendarContext, CalendarContext } from '@components/jobs/contextWrap';
import { LatestOpenType } from '../../index';
import { CalendarPcListItemVO, CalendarPcListVO } from '@domain/vo/calendar/CalendarPcListVO';
import DateFormat from '@utils/date/format';
import ElementUtil from '@utils/elementUtil';

//팝업 공고 모달 width
const POPUP_WiDTH = 424;
//팝업 공고 모달 공고 1개 height
const POPUP_ITEM_HEIGHT = 69;
//팝업 공고 모달 Max height
const POPUP_MAX_HEIGHT = 312;
//캘린더 아이템(회사명) 높이
const CALENDER_ITEM_HEIGHT = 24;
//캘린더 위 높이
const ON_THE_CALENDER_HEIGHT = 172;

interface JobPostCalendarProps {
  calendarInnerRef: React.RefObject<HTMLDivElement>;
  calendarData: CalendarPcListVO;
  isFocusDate: boolean;
  setLatestOpen: (type: LatestOpenType) => void;
  updateBookmarkStatus: (jobPostingSn: number, newBookmarkStatus: boolean) => void;
}

const JobPostCalendar: FC<JobPostCalendarProps> = ({
  calendarData,
  setLatestOpen,
  calendarInnerRef,
  isFocusDate,
  updateBookmarkStatus,
}) => {
  const { calendarInfo, miniCalendarInfo } = useContext(CalendarContext);
  const { filterCondition, jobPostPopup } = useContext(BigCalendarContext);
  const { onOpenJobPostModal, updateCalendarBookmark } = useContext(JobPostModalContext);
  // url 접근시 모달 띄우는 부분
  const { query } = useRouter();
  const jobPostingSn = Number(query.sn);

  const { getAllJobPostingList, getFilteredCalendarData } = useHandleCalendarData({
    calendarDate: calendarInfo.calendarDate,
    bookmarkYn: filterCondition.bookmarkYn,
    jobPostStatus: filterCondition.jobPostStatus,
    aiScreeningYn: filterCondition.aiScreeningYn,
  });

  //필터링된 캘린더 Data
  const filteredCalendarData = getFilteredCalendarData(calendarData.calendarItems);

  const calendarItems = filteredCalendarData?.map((data: CalendarPcListItemVO) => {
    const calendarItemEl = data.companies.map((company) => {
      const itemId = `${DateFormat.format(new Date(data.date), 'yyyy-MM-dd')}-${company.companySn}-${
        company.statusType
      }`;
      const handleEnterHover = () => {
        miniCalendarInfo.setStartDate(company.openingDateTime);
        miniCalendarInfo.setEndDate(company.closingDateTime);
      };

      const handleOutHover = () => {
        miniCalendarInfo.setStartDate(null);
        miniCalendarInfo.setEndDate(null);
      };

      const handleOnClick = () => {
        const isOpenJobPostPopUp = company.jobPostings.length > 1;
        //회사명
        const clickItemInfo = { clickItemId: itemId, height: CALENDER_ITEM_HEIGHT };
        //회사명이 포함된 일
        const clickItemWrapId = DateFormat.format(new Date(data.date), 'yyyy-MM-dd');

        //jobPostingPopup 높이
        const height =
          company.jobPostings.length > 3 ? POPUP_MAX_HEIGHT : POPUP_ITEM_HEIGHT * company.jobPostings.length + 78;
        const popupInfo = { width: POPUP_WiDTH, height };
        updateCalendarBookmark(updateBookmarkStatus);
        //공고들이 많으면 공고 리스트 팝업을 보여줌.
        if (isOpenJobPostPopUp) {
          jobPostPopup.setJobPostPopUpData(company);
          setLatestOpen('JOBPOST');
          jobPostPopup.setJobPostingPopUpPosition(
            ElementUtil.getPopupPosition(calendarInnerRef, clickItemInfo, clickItemWrapId, popupInfo),
          );
        } else {
          //공고가 1개면 공고 모달을 보여줌.
          jobPostPopup.setJobPostPopUpData(null);
          const joPostKey = `${company.jobPostings[0].sn}-${company.statusType}`;
          onOpenJobPostModal(joPostKey, company.jobPostings[0].sn, getAllJobPostingList(calendarData.calendarItems));
        }
      };

      return (
        <Element id={itemId} name={itemId} key={`${company.companySn}-${company.statusType}`}>
          <CalendarItem
            status={company.statusType}
            companyName={company.companyName}
            handleEnterHover={handleEnterHover}
            handleOutHover={handleOutHover}
            onClick={handleOnClick}
          />
        </Element>
      );
    });

    return { date: data.date, child: calendarItemEl };
  });

  //해당 날짜로 캘린더 이동
  const focusSelectedDate = (date: Date) => {
    const findElementId = DateFormat.format(date, 'yyyy-MM-dd');

    scroller.scrollTo(findElementId, {
      offset: -ON_THE_CALENDER_HEIGHT,
    });
  };

  //Mount 후 scroll 위치가 돌아가는 것 방지
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (!isNaN(jobPostingSn)) {
      onOpenJobPostModal(`${jobPostingSn}`, jobPostingSn);
    }
  }, [jobPostingSn]);

  useEffect(() => {
    focusSelectedDate(miniCalendarInfo.selectedDate);
  }, [miniCalendarInfo.selectedDate]);

  return <Calendar calendarItems={calendarItems} isFocusDate={isFocusDate} />;
};

export default JobPostCalendar;
